import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteForm, getFormsByCompany, uploadForm, GetFormsByCompany_Response_Type } from '../axiosNetworkRequest/forms';
import { useSelector } from 'react-redux';
import { ReduxRootStateType } from '../../redux/store';
import { track } from '../../utils/MixpanelHelper';
import useNotification from '../../hooks/useNotification';
import { API_Error_Type } from '../axiosClient/axiosClient';

const FIVE_MINUTES = 5 * 60 * 1000;

export const useGetFormsByCompany = (company_id: number) =>
  useQuery({
    queryKey: ['forms-by-company', company_id],
    queryFn: () => getFormsByCompany(`forms?company_id=${company_id}`),
    staleTime: FIVE_MINUTES,
    enabled: company_id > 0,
  });

export const useUploadForm = (company_id: number) => {
  const queryClient = useQueryClient();
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: (formData: FormData) => uploadForm(formData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forms-by-company', company_id] });
      track('Form Uploaded', {
        'Form Name':    variables.get('form_name'),
        'Uploaded By':  `${userInfo?.first_name} ${userInfo?.last_name}`,
        'Email':        userInfo?.email_id,
        'Company Name': userInfo?.company_name,
      });
    },
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to upload form');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};

export const useDeleteForm = (company_id: number) => {
  const queryClient = useQueryClient();
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: (form_id: number) => deleteForm(form_id),
    onMutate: (form_id) => {
      queryClient.setQueryData(
        ['forms-by-company', company_id],
        (old: any) => old
          ? { ...old, data: old.data.filter((f: any) => f.form_id !== form_id) }
          : old
      );
    },
    onSuccess: (_, form_id) => {
      const cached = queryClient.getQueryData<GetFormsByCompany_Response_Type>(['forms-by-company', company_id]);
      const form   = cached?.data.find(f => f.form_id === form_id);
      track('Form Deleted', {
        'Form Name':    form?.form_name ?? 'unknown',
        'Deleted By':   `${userInfo?.first_name} ${userInfo?.last_name}`,
        'Email':        userInfo?.email_id,
        'Company Name': userInfo?.company_name,
      });
      setMessage('Form deleted successfully.');
      setSeverity('success');
      handleSnackbarOpen();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['forms-by-company', company_id] });
    },
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to delete form');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};
